import * as _ from 'underscore';

import { Formatters, Messages } from '../../codegen/Localize';
import { IActionContext } from '../helpers/ActionContext';
import { ResultHelper } from '../helpers/ResultHelper';
import { L10n } from '../L10n/L10n';
import {
  DataAlertDefinition,
  DataRoleDefinition,
  DatasourceDefinition,
  FlowDefinition,
  ProjectDefinition,
  PublishedConnectionDefinition,
  WorkbookDefinition,
  CollectionDefinition,
} from '../models/ContentDefinitions';
import { MetricDefinition } from '../../metrics/models/MetricDefinition';
import { FlowListItemInfo } from '../models/FlowInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataAlertBase } from '../models/IDataAlertInfo';
import { IDataRoleInfo } from '../models/IDataRoleInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { IProjectBase } from '../models/IProjectInfo';
import { IPublishedConnectionInfo } from '../models/IPublishedConnectionInfo';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataAlerts } from '../resources/DataAlerts';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../resources/Datasources';
import { Flows } from '../resources/Flows';
import { Metrics } from '../../metrics/resources/Metrics';
import { Projects } from '../resources/Projects';
import { PublishedConnections } from '../resources/PublishedConnections';
import { Workbooks } from '../resources/Workbooks';
import { IResult } from '../ServerApi/types/IResponse';
import { IUserName } from '../ServerApi/types/IUser';
import { ActionHelpers } from './ActionHelpers';
import { DataAlertErrors } from './DataAlertErrors';
import { DataRoleErrors } from './DataRoleErrors';
import { DataSourceErrors } from './DataSourceErrors';
import { FlowErrors } from './FlowErrors';
import { MetricErrors } from '../../metrics/services/MetricErrors';
import { ProjectErrors } from './ProjectErrors';
import { PublishedConnectionErrors } from './PublishedConnectionErrors';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { WorkbookErrors } from './WorkbookErrors';
import { SetOwnerDialogBodyProps, SetOwnerDialogState, SetOwnerDialogBody } from '../../app/content/SetOwnerDialog';
import { openConfirmActionDialog } from './ReactConfirmActionDialog';
import { ICollectionInfo } from '../../app/spaces/Collections/ICollectionInfo';
import { CollectionsResourceQuery } from '../../app/spaces/Collections/Collections';

export class ChangeContentOwnerAction {
  public static $inject = [
    '$q',
    'ActionHelpers',
    'DataRoleErrors',
    'DataRoles',
    'DataSourceErrors',
    'DataSources',
    'Flows',
    'FlowErrors',
    'ProjectErrors',
    'Projects',
    'SearchSuggestionsService',
    'WorkbookErrors',
    'Workbooks',
    'DataAlerts',
    'DataAlertErrors',
    'Metrics',
    'PublishedConnections',
  ];

  constructor(
    private $q: ng.IQService,
    private ActionHelpers: ActionHelpers,
    private DataRoleErrors: DataRoleErrors,
    private DataRoles: DataRoles,
    private DataSourceErrors: DataSourceErrors,
    private DataSources: DataSources,
    private Flows: Flows,
    private FlowErrors: FlowErrors,
    private ProjectErrors: ProjectErrors,
    private Projects: Projects,
    private SearchSuggestionsService: SearchSuggestionsService,
    private WorkbookErrors: WorkbookErrors,
    private Workbooks: Workbooks,
    private DataAlerts: DataAlerts,
    private DataAlertErrors: DataAlertErrors,
    private Metrics: Metrics,
    private PublishedConnections: PublishedConnections,
  ) {}

  public confirmChangeOwner(content: IContentInfo[], _context: IActionContext): ng.IPromise<IResult> {
    const containsOneWorkbook = !_.isUndefined(content.find((info) => info.contentType === 'workbook'));
    const count = content.length;
    const modalInstance = openConfirmActionDialog<SetOwnerDialogBodyProps, SetOwnerDialogState, IResult>({
      title:  Messages.changeOwner(),
      bodyComponent: SetOwnerDialogBody,
      initialBodyProps: {
        prompt: getPromptForContent(content),
        showCredentialsWarning: containsOneWorkbook,
        getQuery: (matchText: string) => this.getQuery(content, matchText)
      },
      initialDialogState: { targetOwner: null },
      actionName: Messages.changeOwnerConfirm({ COUNT: count, COUNT_LOC: Formatters.formatCount(count) }),
      action: (_bodyProps: SetOwnerDialogBodyProps, dialogState: SetOwnerDialogState) => {
        return this.setOwnerForContent(content, dialogState.targetOwner).then((results) => {
          return ResultHelper.combineResults(results);
        });
      },
      actionDisabled: (_bodyProps: SetOwnerDialogBodyProps, dialogState: SetOwnerDialogState) => dialogState.targetOwner == null,
    });

    return modalInstance.result;
  }

  private getQuery(content: IContentInfo[], matchText: string) {
    const containsProjects = content.find(i => i.contentType === ProjectDefinition.contentType) !== undefined;
    if (containsProjects) {
      return this.SearchSuggestionsService.usersCanOwnProject(matchText);
    }
    return this.SearchSuggestionsService.usersWithoutGuest(matchText);
  }

  // public for testing only!  Calling this bypasses the confirm dialog UI.
  public setOwnerForContent(content: IContentInfo[], owner: IUserName) {
    const projects = Set.fromIdItemArray(content.filter(i => i.contentType === ProjectDefinition.contentType));
    const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === WorkbookDefinition.contentType));
    const datasources = Set.fromIdItemArray(content.filter(i => i.contentType === DatasourceDefinition.contentType));
    const flows = Set.fromIdItemArray(content.filter(i => i.contentType === FlowDefinition.contentType));
    const dataroles = Set.fromIdItemArray(content.filter(i => i.contentType === DataRoleDefinition.contentType));
    const alerts = Set.fromIdItemArray(content.filter(i => i.contentType === DataAlertDefinition.itemType));
    const metrics = Set.fromIdItemArray(content.filter(i => i.contentType === MetricDefinition.itemType));
    const publishedConnections = Set.fromIdItemArray(content.filter(i => i.contentType === PublishedConnectionDefinition.contentType));
    const collections = Set.fromIdItemArray(content.filter(i => i.contentType === CollectionDefinition.contentType));

    return this.$q.all([
      this.setOwnerForProjects(projects, owner),
      this.setOwnerForWorkbooks(workbooks, owner),
      this.setOwnerForDatasources(datasources, owner),
      this.setOwnerForFlows(flows, owner),
      this.setOwnerForDataRoles(dataroles, owner),
      this.setOwnerForDataAlerts(alerts, owner),
      this.setOwnerForMetrics(metrics, owner),
      this.setOwnerForPublishedConnections(publishedConnections, owner),
      this.setOwnerForCollections(collections, owner),
    ]);
  }

  private setOwnerForProjects(projects: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (projects.size() < 1) {
      return this.$q.when(null);
    }

    return this.Projects.setOwner(projects.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        projects,
        { successId: 'changeOwnerProjectsSuccess', errorId: 'changeOwnerProjectsError' },
        (errorItem: IErrorItem<IProjectBase>) => this.ProjectErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForMetrics(metrics: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (metrics.size() < 1) {
      return this.$q.when(null);
    }

    return this.Metrics.setOwner(metrics.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        metrics,
        { successId: 'changeOwnerForMetricsSuccess', errorId: 'changeOwnerForMetricsError' },
        (errorItem: IErrorItem<IMetricInfo>) => MetricErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForPublishedConnections(publishedConnections: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (publishedConnections.size() < 1) {
      return this.$q.when(null);
    }

    return this.PublishedConnections.setOwner(publishedConnections.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        publishedConnections,
        { successId: 'changeOwnerPublishedConnectionsSuccess', errorId: 'changeOwnerPublishedConnectionsError' },
        (errorItem: IErrorItem<IPublishedConnectionInfo>) => PublishedConnectionErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForWorkbooks(workbooks: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (workbooks.size() < 1) {
      return this.$q.when(null);
    }

    return this.Workbooks.setOwner(workbooks.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        workbooks,
        { successId: 'changeOwnerWorkbooksSuccess', errorId: 'changeOwnerWorkbooksError' },
        (errorItem: IErrorItem<IWorkbookBase>) => this.WorkbookErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForDatasources(datasources: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (datasources.size() < 1) {
      return this.$q.when(null);
    }

    return this.DataSources.setOwner(datasources.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        datasources,
        { successId: 'changeOwnerDatasourcesSuccess', errorId: 'changeOwnerDatasourcesError' },
        (errorItem: IErrorItem<IDataSourceBase>) => this.DataSourceErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForFlows(flows: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (flows.size() < 1) {
      return this.$q.when(null);
    }

    return this.Flows.setOwner(flows.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        flows,
        { successId: 'changeOwnerFlowsSuccess', errorId: 'changeOwnerFlowsError' },
        (errorItem: IErrorItem<FlowListItemInfo>) => this.FlowErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForDataRoles(dataroles: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (dataroles.size() < 1) {
      return this.$q.when(null);
    }

    return this.DataRoles.setOwner(dataroles.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        dataroles,
        { successId: 'changeOwnerDataRolesSuccess', errorId: 'changeOwnerDataRolesError' },
        (errorItem: IErrorItem<IDataRoleInfo>) => this.DataRoleErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForDataAlerts(alerts: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (alerts.size() < 1) {
      return this.$q.when(null);
    }

    return this.DataAlerts.setOwner(alerts.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError<IDataAlertBase>(
        alerts as Set<IDataAlertBase>,
        { successId: 'changeOwnerDataAlertsSuccess', errorId: 'changeOwnerDataAlertsError' },
        (errorItem: IErrorItem<IDataAlertBase>) => this.DataAlertErrors.errorDetailFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForCollections(collections: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (collections.size() < 1) {
      return this.$q.when(null);
    }

    const updateModel = {
      ownerLuid: owner.luid,
    };

    // There is no batch-update for collections
    // So we call update individually for each collection
    // and cast the result http errors into IError.
    const promises = collections.map((collection) => {
      return new CollectionsResourceQuery().update(collection.luid, updateModel).then(() => {
        return {} as IResult;
      }).catch((error: any)=> {
        return {errors: [{code: error.data.errorCode, id: collection.id}]} as IResult;
      });
    });
    return this.$q.all(promises).then((results: IResult[]) => {
      const result = ResultHelper.combineResults(results);
      return this.ActionHelpers.notifyStandardError<ICollectionInfo>(
        collections as Set<ICollectionInfo>,
        { successId: 'CollectionsChangeOwnerSuccess', errorId: 'CollectionsChangeOwnerError' },
        (errorItem: IErrorItem<ICollectionInfo>) => ({itemName: errorItem.item.name, errorMessage: Messages.unexpectedError()}),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }
}

function getPromptForContent(content: IContentInfo[]): string {
  if (content.length === 1) {
    const item = content[0];
    return Messages.changeOwnerContentItemPrompt({ NAME: L10n.elideIfLong(item.name) });
  } else {
    return Messages.changeOwnerContentItemsPrompt({ COUNT_LOC: Formatters.formatCount(content.length) });
  }
}

Module.instance().service('ChangeContentOwnerAction', ChangeContentOwnerAction);
